import type { CostDashboardFiltersResponse } from "../../types/summary.types";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchWeeklyCostReport, fetchWeeklyCustomerReport, } from "../../store/costAnalyticsSlice";
import { fetchCostDashboardFilters } from "../../store/costSummarySlice";
import type { WeeklyCostReportResponse } from "../../types/weekly-cost-report.types";

import { ALL_CUSTOMERS_VALUE } from "@/features/cost/constants/weekly-cost.constants";

interface UseWeeklyCostDataParams {
  weekStartDate: string ;
  selectedCustomer: string;
}

interface UseWeeklyCostDataReturn {
  weeklyReportData: WeeklyCostReportResponse | null;
  weeklyReportLoading: boolean;
  weeklyReportError: string | null;
  weeklyCustomerReportData: WeeklyCostReportResponse | null;
  weeklyCustomerReportLoading: boolean;
  weeklyCustomerReportError: string | null;
  customerRgFilters: CostDashboardFiltersResponse | null;
  customerRgFiltersLoading: boolean;
}

export const useWeeklyCostData = ({
  weekStartDate,
  selectedCustomer,
}: UseWeeklyCostDataParams): UseWeeklyCostDataReturn => {
  const dispatch = useAppDispatch();

  const {
    weeklyReportData,
    weeklyReportLoading,
    weeklyReportError,
    weeklyCustomerReportData,
    weeklyCustomerReportLoading,
    weeklyCustomerReportError,
  } = useAppSelector((state) => state.costAnalytics);

  const { customerRgFilters, customerRgFiltersLoading } = useAppSelector(
    (state) => state.costSummary,
  );

  // Fetch dashboard filters on mount
  useEffect(() => {
    if (!customerRgFilters && !customerRgFiltersLoading) {
      dispatch(fetchCostDashboardFilters({ subscriptionIds: [] }));
    }
  }, [customerRgFilters, customerRgFiltersLoading, dispatch]);

  // Load the main report when weekStartDate changes
  useEffect(() => {
    dispatch(fetchWeeklyCostReport({ weekStartDate }));
  }, [dispatch, weekStartDate]);

  // Load customer insights when customer changes
  useEffect(() => {
    if (selectedCustomer !== ALL_CUSTOMERS_VALUE) {
      dispatch(
        fetchWeeklyCustomerReport({
          weekStartDate,
          customerName: selectedCustomer,
        }),
      );
    }
  }, [dispatch, selectedCustomer, weekStartDate]);

  return {
    weeklyReportData,
    weeklyReportLoading,
    weeklyReportError,
    weeklyCustomerReportData,
    weeklyCustomerReportLoading,
    weeklyCustomerReportError,
    customerRgFilters,
    customerRgFiltersLoading,
  };
};
